import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EPL_TEAMS } from "@/data/merchCups";
import { describeCupLine, formatKes, normalizeCupLine } from "@/lib/merchPricing";
import { getMerchOrder } from "@/lib/merchApi";
import { sendMerchWhatsAppReceipt } from "@/lib/whatsappReceipt";
import { markMerchOrderPaidSupabase } from "@/lib/merchOrdersSupabase";
import { initiateChepparMpesa } from "@/lib/mpesaPayment";
import {
    supabase,
    isSupabaseConfigured,
    sanitizeSupabaseError,
} from "@/utils/supabase/client";

const PHONE_REGEX = /^(?:\+254|0)7\d{8}$/;

export default function MerchOrderPay() {
    const { orderId } = useParams();
    const navigate = useNavigate();
    const [order, setOrder] = useState(null);
    const [phoneNumber, setPhoneNumber] = useState("");
    const [loading, setLoading] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);
    const [showPaymentSection, setShowPaymentSection] = useState(false);
    const pollActiveRef = useRef(true);

    const fulfillPaidOrder = useCallback(
        async (paymentReference) => {
            if (!order?.external_reference) {
                throw new Error("Order reference missing.");
            }
            const updated = await markMerchOrderPaidSupabase(
                order.external_reference,
                paymentReference,
            );
            const orderForReceipt = {
                ...updated,
                mpesa_receipt: paymentReference,
            };

            try {
                await sendMerchWhatsAppReceipt(orderForReceipt);
                setMessage("Payment received — receipt sent to your WhatsApp.");
                toast.success("Payment confirmed! Check WhatsApp for your receipt.");
            } catch (receiptErr) {
                console.error("WhatsApp receipt failed:", receiptErr);
                setMessage(
                    "Payment received. Receipt could not be sent automatically — we will contact you on WhatsApp.",
                );
                toast.success("Payment confirmed!");
                toast.error(
                    receiptErr.message ||
                        "WhatsApp receipt failed. Contact support with your order ID.",
                );
            }

            window.setTimeout(() => {
                navigate(`/merch/order/${orderId}/confirm`);
            }, 2000);
        },
        [navigate, orderId, order?.external_reference],
    );

    const load = useCallback(async () => {
        try {
            const data = await getMerchOrder(orderId);
            setOrder(data);
            setPhoneNumber(data.phone || "");
            if (data.status === "paid") {
                navigate(`/merch/order/${data.id}/confirm`, { replace: true });
            }
        } catch {
            toast.error("Order not found.");
        } finally {
            setLoading(false);
        }
    }, [orderId, navigate]);

    useEffect(() => {
        pollActiveRef.current = true;
        load();
        return () => {
            pollActiveRef.current = false;
        };
    }, [load]);

    const handleCheckout = () => {
        if (!order?.external_reference) {
            setError(
                "This order is missing a payment reference. Please place a new order.",
            );
            return;
        }
        if (!isSupabaseConfigured()) {
            setError(
                "Supabase is not configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.",
            );
            return;
        }
        setError(null);
        setShowPaymentSection(true);
    };

    const pollPaymentStatus = useCallback(
        async (userReference, retryCount = 15, interval = 10000) => {
            if (!pollActiveRef.current) return;

            if (retryCount <= 0) {
                setIsProcessing(false);
                setError(
                    "Payment verification timed out. Please check your M-Pesa balance or try again.",
                );
                return;
            }

            try {
                const { data: paymentData, error: paymentError } = await supabase
                    .from("gaspayments")
                    .select("status, mpesa_reference")
                    .eq("user_reference", userReference)
                    .single();

                if (paymentError || !paymentData) {
                    window.setTimeout(() => {
                        pollPaymentStatus(userReference, retryCount - 1, interval);
                    }, interval);
                    return;
                }

                if (paymentData.status === true) {
                    setIsProcessing(false);
                    await fulfillPaidOrder(paymentData.mpesa_reference);
                    return;
                }

                window.setTimeout(() => {
                    pollPaymentStatus(userReference, retryCount - 1, interval);
                }, interval);
            } catch (pollErr) {
                setIsProcessing(false);
                setError(
                    pollErr.message ||
                        "There was an error checking your payment status.",
                );
            }
        },
        [fulfillPaidOrder, order],
    );

    const handleMpesaPayment = async () => {
        if (!phoneNumber.trim()) {
            setError("Please enter your M-Pesa phone number.");
            return;
        }

        if (!PHONE_REGEX.test(phoneNumber.replace(/\s/g, ""))) {
            setError(
                "Please enter a valid Kenyan number (e.g. 0712345678 or +254712345678).",
            );
            return;
        }

        if (!order?.external_reference) {
            setError("This order is missing a payment reference. Place a new order.");
            return;
        }

        setIsProcessing(true);
        setMessage(null);
        setError(null);

        try {
            await initiateChepparMpesa({
                phoneNumber,
                amount: order.total_kes,
                externalReference: order.external_reference,
            });

            setMessage("Check your phone for the M-Pesa prompt.");

            const { error: supabaseError } = await supabase
                .from("merch_orders")
                .update({ phone: phoneNumber, status: "pending" })
                .eq("external_reference", order.external_reference);

            if (supabaseError) {
                throw new Error(
                    sanitizeSupabaseError(supabaseError, "Failed to update order."),
                );
            }

            window.setTimeout(() => {
                pollPaymentStatus(order.external_reference);
            }, 5000);
        } catch (err) {
            setIsProcessing(false);
            setError(
                err.message ||
                    "There was an error processing your M-Pesa request.",
            );
        }
    };

    if (loading) {
        return (
            <main className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
                <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
            </main>
        );
    }

    if (!order) {
        return (
            <main className="min-h-screen bg-[#FAFAFA] px-6 pt-32 text-center">
                <p className="text-zinc-600">Order not found.</p>
                <Link
                    to="/merch/cups"
                    className="text-[#E54D2E] underline mt-4 inline-block"
                >
                    Back to cups
                </Link>
            </main>
        );
    }

    return (
        <main
            data-testid="merch-pay-page"
            className="min-h-screen bg-[#FAFAFA] text-zinc-950"
        >
            <Header />
            <div className="pt-24 sm:pt-28 pb-16 mx-auto max-w-lg px-4 sm:px-6">
                <div className="rounded-2xl border border-zinc-200 bg-white/90 p-6 shadow-md backdrop-blur-sm">
                    <h1 className="font-display text-2xl font-black text-center mb-2">
                        Pay for your order
                    </h1>
                    <p className="text-sm text-zinc-500 text-center mb-6">
                        M-Pesa only
                    </p>

                    <div className="space-y-4">
                        <div className="rounded-xl border border-zinc-100 bg-zinc-50 p-4 space-y-2 text-sm">
                            <p>
                                <span className="text-zinc-500">Order</span>{" "}
                                <span className="font-mono font-semibold">
                                    {order.id}
                                </span>
                            </p>
                            <p>
                                {order.cups} cups · {order.color} · {order.packs}{" "}
                                pack{order.packs !== 1 ? "s" : ""}
                            </p>
                            <ul className="text-zinc-700 space-y-0.5">
                                {(order.cup_teams || []).map((entry, i) => (
                                    <li key={i}>
                                        Cup {i + 1}:{" "}
                                        {describeCupLine(
                                            normalizeCupLine(entry),
                                            EPL_TEAMS,
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div>
                            <p className="text-lg font-medium text-zinc-800">
                                Total amount
                            </p>
                            <p className="text-zinc-600">
                                {formatKes(order.total_kes)}
                            </p>
                        </div>

                        {order.status === "pending" && !showPaymentSection && (
                            <Button
                                type="button"
                                onClick={handleCheckout}
                                disabled={isProcessing}
                                className="w-full rounded-full bg-zinc-950 text-white hover:bg-[#E54D2E]"
                            >
                                Continue to payment
                            </Button>
                        )}

                        {order.status === "pending" && showPaymentSection && (
                            <div className="space-y-4">
                                <div className="rounded-xl border border-[#E54D2E]/20 bg-[#E54D2E]/5 p-4 text-sm text-zinc-700">
                                    <p className="font-medium text-[#E54D2E] mb-2">
                                        How to pay with M-Pesa
                                    </p>
                                    <ol className="list-decimal list-inside space-y-1">
                                        <li>Enter your M-Pesa number below.</li>
                                        <li>
                                            Tap Pay — you will get an STK prompt on
                                            your phone.
                                        </li>
                                        <li>
                                            Enter your M-Pesa PIN to confirm.
                                        </li>
                                        <li>
                                            We send your receipt on WhatsApp when
                                            paid.
                                        </li>
                                    </ol>
                                </div>

                                <div>
                                    <Label
                                        htmlFor="phone"
                                        className="text-sm font-medium text-zinc-700"
                                    >
                                        M-Pesa phone number
                                    </Label>
                                    <Input
                                        id="phone"
                                        type="tel"
                                        placeholder="e.g. 0712345678"
                                        value={phoneNumber}
                                        onChange={(e) =>
                                            setPhoneNumber(e.target.value)
                                        }
                                        disabled={isProcessing}
                                        className="mt-1"
                                    />
                                </div>

                                <Button
                                    type="button"
                                    onClick={handleMpesaPayment}
                                    disabled={isProcessing}
                                    className="w-full rounded-full bg-[#16A34A] text-white hover:bg-zinc-950"
                                >
                                    {isProcessing
                                        ? "Processing…"
                                        : `Pay ${order.total_kes.toLocaleString("en-KE")} KES`}
                                </Button>
                            </div>
                        )}

                        {message && (
                            <p className="text-green-600 text-sm text-center">
                                {message}
                            </p>
                        )}
                        {error && (
                            <p className="text-red-600 text-sm text-center">
                                {error}
                            </p>
                        )}
                        {isProcessing && (
                            <div className="flex flex-col items-center gap-2">
                                <div className="w-6 h-6 border-4 border-t-green-500 border-zinc-200 rounded-full animate-spin" />
                                <p className="text-xs text-zinc-500">
                                    Waiting for M-Pesa confirmation…
                                </p>
                            </div>
                        )}

                        <p className="text-xs text-zinc-500 text-center">
                            Receipt sent to WhatsApp when payment is confirmed.
                        </p>
                    </div>
                </div>

                <Link
                    to="/merch/cups"
                    className="block text-center text-sm text-zinc-500 mt-8 hover:text-zinc-950"
                >
                    ← Order more cups
                </Link>
            </div>
            <Footer />
        </main>
    );
}
